import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SearchInput from "@insite/content-library/Widgets/Header/SearchInput";
import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon/Icon";
import { InputStyle, TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowRightWhiteIcon from "@tCentro/Icons/ArrowRightWhiteIcon";
import React from "react";
import { FC } from "react";
import { Link } from "react-router-dom";
import { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
    heading = "heading",
    subheading = "subheading",
    btnLabel = "btnLabel",
    btnlink = "btnlink",
    backgroundImage = "backgroundImage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.btnLabel]: string;
        [fields.btnlink]: LinkFieldValue;
        [fields.backgroundImage]: string;
    };
}

type Props = OwnProps;

export interface HomePageTopBannerStyles {
    headingStyles: InjectableCss;
    subheadingContainerStyles: InjectableCss;
    subheadingStyles: InjectableCss;
    searchbarStyles: TextFieldPresentationProps;
    btnStyles: InjectableCss;
    iconStyle: InjectableCss;
    contentContainerStyles: InjectableCss;
    homePageBlockStyles: InjectableCss;
}

export const homePageTopBannerStyles: HomePageTopBannerStyles = {
    homePageBlockStyles: {
        css: css`
            align-self: flex-start;
            margin-left: 64px;
            width: 90%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-left: 21px;
                    `,
                    css`
                        margin-left: 21px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    headingStyles: {
        css: css`
            font-size: 40px;
            line-height: 48px;
            font-family: "Poppins";
            font-weight: 800;
        `,
    },
    subheadingStyles: {
        css: css`
            font-size: 18px;
            line-height: 24px;
            font-weight: 400;
        `,
    },
    searchbarStyles: {
        css: css`
            width: 90%;
            margin: 32px auto 32px auto;
            > div[class^="OverflowWrapper"] {
                > div[class^="sc"] {
                    > ${FormFieldStyle} {
                        > ${FormInputWrapper} {
                            > ${InputStyle} {
                                box-shadow: none;
                                border: none !important;
                            }
                        }
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    subheadingContainerStyles: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            margin-top: 16px;
        `,
    },
    btnStyles: {
        css: css`
            cursor: pointer;
            font-weight: 600;
            margin: 8px auto;
            font-size: 18px;
            line-height: 24px;
        `,
    },
    iconStyle: {
        css: css`
            height: 11.25px;
            width: 18px;
            margin-left: 4px;
            svg {
                height: 11.25px;
                width: 18px;
            }
        `,
    },
    contentContainerStyles: {
        css: css`
            padding: 64px 64px 0px 64px;
        `,
    },
};

const styles = homePageTopBannerStyles;

const CENHomePageTopBanner: FC<Props> = ({ fields, id }) => {
    const backgroundStyles = fields.backgroundImage
        ? `background: url(${fields.backgroundImage}); background-size: cover; background-repeat: no-repeat;`
        : `background:linear-gradient(90deg, #266b40 27.08%, #2d804c 71.87%, #43687a 100%);`;

    const container = {
        css: css`
            ${backgroundStyles};
            width: 100%;
            color: white;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            text-align: center;
        `,
    };

    const { url, title } = useGetLink(fields.btnlink);
    const history = useHistory();
    const { isInShell } = useShellContext();
    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };
    return (
        <StyledWrapper {...container}>
            <StyledWrapper {...styles.contentContainerStyles}>
                <StyledWrapper {...styles.headingStyles}>{fields.heading}</StyledWrapper>
                <StyledWrapper {...styles.subheadingContainerStyles}>
                    <StyledWrapper {...styles.subheadingStyles}>{fields.subheading}</StyledWrapper>
                    <Typography {...styles.btnStyles} onClick={onClick}>
                        {fields.btnLabel || title || url}
                        {fields.btnLabel.length > 0 && <Icon src={ArrowRightWhiteIcon} {...styles.iconStyle} />}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.searchbarStyles}>
                <SearchInput id={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.homePageBlockStyles}>
                <Zone contentId={id} zoneName="homePageBlock"></Zone>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const CENhomePageTopBanner: WidgetModule = {
    component: CENHomePageTopBanner,
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.heading,
                editorTemplate: "TextField",
                displayName: "Heading",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.subheading,
                editorTemplate: "TextField",
                displayName: "SubHeading",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.btnLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.btnlink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
            },
            {
                name: fields.backgroundImage,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Background Image",
            },
        ],
    },
};
export default CENhomePageTopBanner;
